import { SkipLink } from '@civilgrid/ui/components';
import { KnockProvider } from '@knocklabs/react';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useSnapshot } from 'valtio';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import getConfig from '~/config';
import { useIsSandboxOrg } from '~/hooks/useIsSandboxOrg';
import rootStore from '~/store';
import AnnouncementModal from './announcements/Announcement';
import { projectTiersAnnouncement } from './announcements/announcements';
import SandboxBanner from './banners/SandboxBanner';
import UnverifiedBanner from './banners/UnverifiedBanner';
import SubscriptionInactiveBanner, { useShowSubscriptionInactiveBanner } from './SubscriptionInactiveBanner';
import TopNav, { type TopNavProps } from './TopNav';

export const MAIN_CONTENT_ID = 'main-content';

interface Props extends TopNavProps {
  fluidHeight?: boolean;
  children: ReactNode;
}

export default function ProtectedRouteLayout({ fluidHeight = false, children, ...props }: Props) {
  const isSandboxOrg = useIsSandboxOrg();
  const subscriptionBannerWanted = useShowSubscriptionInactiveBanner();
  const { inUnverifiedMode, me, disclaimerViewed, inOnboardingMode } = useSnapshot(rootStore);
  const config = getConfig();
  const hasAccountBanner = isSandboxOrg() || inUnverifiedMode;
  // One banner at a time; the fluid main height below reserves room for a single h-8 banner.
  const showSubscriptionBanner = subscriptionBannerWanted && !hasAccountBanner;
  const hasBanner = hasAccountBanner || showSubscriptionBanner;
  if (!me) {
    return null;
  }

  // 7rem below is the TopNav height plus the SandboxBanner height.
  return (
    <KnockProvider
      apiKey={config.knockPublicKey}
      user={{ id: me.id }}
      userToken={me.knockToken}
      // The server identifies users when it triggers workflows; skip the client-side identify call on every mount.
      identificationStrategy="skip"
    >
      <div className="flex h-screen w-full flex-col">
        <SkipLink
          targetId={MAIN_CONTENT_ID}
          data-testid="skip-to-main-content-link"
          onClick={() => capturePosthogEvent(POSTHOG_EVENTS.skipLinkActivated, { page_path: window.location.pathname })}
        >
          Skip to main content
        </SkipLink>
        <header className="flex shrink-0 flex-col">
          {isSandboxOrg() && <SandboxBanner />}
          {disclaimerViewed && !inOnboardingMode && <AnnouncementModal announcement={projectTiersAnnouncement} />}
          {inUnverifiedMode && <UnverifiedBanner />}
          <TopNav {...props} />
          {showSubscriptionBanner && <SubscriptionInactiveBanner />}
        </header>
        <main
          id={MAIN_CONTENT_ID}
          tabIndex={-1}
          className={classNames(
            'outline-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:-outline-offset-3 focus-visible:outline-focus',
            {
              'h-screen-height-minus-top-nav flex-grow': !fluidHeight && !hasBanner,
              'min-h-0 flex-1': !fluidHeight && hasBanner,
              'h-auto overflow-auto': fluidHeight,
              'h-[calc(100%-5rem)]': fluidHeight && hasBanner,
            },
          )}
        >
          {children}
        </main>
      </div>
    </KnockProvider>
  );
}
